import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  FormHelperText,
} from '@mui/material';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { useFindOrCreateReservationPartyMutation } from '../../Service/extendedReservationsApi';
import type { ReservationPartyLookupDto, ReservationPartySelectorField } from '../../Types/reservationTypes';
import { extractEntityFormApiErrors } from '../../../Shared/utils/entityFormApiErrorHandling';

const FIELD_LABELS: Record<ReservationPartySelectorField, string> = {
  applicantPartyId: 'stronę zgłaszającą',
  invoiceBuyerPartyId: 'stronę faktury',
  fundingPartyId: 'stronę finansującą',
};

const schema = yup
  .object({
    partyType: yup
      .string()
      .oneOf(['individual', 'organization'] as const)
      .required('Wybierz typ strony'),
    firstName: yup
      .string()
      .default('')
      .when('partyType', {
        is: 'individual',
        then: (field) => field.required('Imię jest wymagane'),
      }),
    lastName: yup
      .string()
      .default('')
      .when('partyType', {
        is: 'individual',
        then: (field) => field.required('Nazwisko jest wymagane'),
      }),
    organizationName: yup
      .string()
      .default('')
      .when('partyType', {
        is: 'organization',
        then: (field) => field.required('Nazwa firmy jest wymagana'),
      }),
    taxId: yup
      .string()
      .default('')
      .when('partyType', {
        is: 'organization',
        then: (field) => field.required('NIP jest wymagany'),
      }),
    regon: yup.string().default(''),
    email: yup.string().email('Nieprawidłowy adres email').default(''),
    phone: yup.string().default(''),
    street: yup.string().default(''),
    city: yup.string().default(''),
    postalCode: yup.string().default(''),
    countryCode: yup.string().default('PL'),
  });

type AddPartyFormValues = yup.InferType<typeof schema>;

interface AddReservationPartyModalProps {
  open: boolean;
  field: ReservationPartySelectorField;
  onClose: () => void;
  onCreated: (party: ReservationPartyLookupDto, field: ReservationPartySelectorField) => void;
}

const EMPTY_VALUES: AddPartyFormValues = {
  partyType: 'individual',
  firstName: '',
  lastName: '',
  organizationName: '',
  taxId: '',
  regon: '',
  email: '',
  phone: '',
  street: '',
  city: '',
  postalCode: '',
  countryCode: 'PL',
};

const AddReservationPartyModal = ({
  open,
  field,
  onClose,
  onCreated,
}: AddReservationPartyModalProps) => {
  const [findOrCreate, { isLoading }] = useFindOrCreateReservationPartyMutation();
  const { enqueueSnackbar } = useSnackbar();
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<AddPartyFormValues>({
    resolver: yupResolver(schema),
    defaultValues: EMPTY_VALUES,
  });

  const partyType = useWatch({ control, name: 'partyType' });

  useEffect(() => {
    if (!open) {
      reset(EMPTY_VALUES);
      setSubmitErrorMessage(null);
    }
  }, [open, reset]);

  const onSubmit = async (values: AddPartyFormValues) => {
    try {
      setSubmitErrorMessage(null);
      const result = await findOrCreate({
        partyType: values.partyType as 'individual' | 'organization',
        ...(values.partyType === 'individual'
          ? { firstName: values.firstName || undefined, lastName: values.lastName || undefined }
          : {
              organizationName: values.organizationName || undefined,
              taxId: values.taxId || undefined,
              regon: values.regon || undefined,
            }),
        email: values.email || undefined,
        phone: values.phone || undefined,
        street: values.street || undefined,
        city: values.city || undefined,
        postalCode: values.postalCode || undefined,
        countryCode: values.countryCode || undefined,
      }).unwrap();
      enqueueSnackbar(`Pomyślnie dodano ${FIELD_LABELS[field]}.`, { variant: 'success' });
      onCreated(result, field);
      onClose();
    } catch (error) {
      const { submitErrorMessage, fieldErrors } = extractEntityFormApiErrors(
        error,
        'Nie udało się zapisać strony. Spróbuj ponownie.'
      );
      Object.entries(fieldErrors).forEach(([fieldName, message]) => {
        setError(fieldName as keyof AddPartyFormValues, {
          type: 'server',
          message,
        });
      });
      setSubmitErrorMessage(submitErrorMessage);
      enqueueSnackbar(submitErrorMessage, { variant: 'error' });
    }
  };

  const label = FIELD_LABELS[field];
  const hasFieldErrors = Object.keys(errors).length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Dodaj nową {label}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Controller
              name="partyType"
              control={control}
              render={({ field: f }) => (
                <FormControl error={!!errors.partyType}>
                  <FormLabel>Typ strony</FormLabel>
                  <RadioGroup row {...f}>
                    <FormControlLabel value="individual" control={<Radio />} label="Osoba fizyczna" />
                    <FormControlLabel value="organization" control={<Radio />} label="Firma" />
                  </RadioGroup>
                  {errors.partyType && (
                    <FormHelperText>{errors.partyType.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />

            {partyType === 'individual' && (
              <Stack direction="row" spacing={2}>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field: f }) => (
                    <TextField
                      {...f}
                      label="Imię"
                      required
                      fullWidth
                      error={!!errors.firstName}
                      helperText={errors.firstName?.message}
                    />
                  )}
                />
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field: f }) => (
                    <TextField
                      {...f}
                      label="Nazwisko"
                      required
                      fullWidth
                      error={!!errors.lastName}
                      helperText={errors.lastName?.message}
                    />
                  )}
                />
              </Stack>
            )}

            {partyType === 'organization' && (
              <>
                <Controller
                  name="organizationName"
                  control={control}
                  render={({ field: f }) => (
                    <TextField
                      {...f}
                      label="Nazwa firmy"
                      required
                      fullWidth
                      error={!!errors.organizationName}
                      helperText={errors.organizationName?.message}
                    />
                  )}
                />
                <Stack direction="row" spacing={2}>
                  <Controller
                    name="taxId"
                    control={control}
                    render={({ field: f }) => (
                      <TextField
                        {...f}
                        label="NIP"
                        required
                        fullWidth
                        error={!!errors.taxId}
                        helperText={errors.taxId?.message}
                      />
                    )}
                  />
                  <Controller
                    name="regon"
                    control={control}
                    render={({ field: f }) => (
                      <TextField {...f} label="REGON" fullWidth />
                    )}
                  />
                </Stack>
              </>
            )}

            <Stack direction="row" spacing={2}>
              <Controller
                name="email"
                control={control}
                render={({ field: f }) => (
                  <TextField
                    {...f}
                    label="Email"
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />
              <Controller
                name="phone"
                control={control}
                render={({ field: f }) => (
                  <TextField {...f} label="Telefon" fullWidth />
                )}
              />
            </Stack>

            <Controller
              name="street"
              control={control}
              render={({ field: f }) => (
                <TextField {...f} label="Ulica" fullWidth />
              )}
            />

            <Stack direction="row" spacing={2}>
              <Controller
                name="city"
                control={control}
                render={({ field: f }) => (
                  <TextField {...f} label="Miasto" fullWidth />
                )}
              />
              <Controller
                name="postalCode"
                control={control}
                render={({ field: f }) => (
                  <TextField {...f} label="Kod pocztowy" fullWidth />
                )}
              />
              <Controller
                name="countryCode"
                control={control}
                render={({ field: f }) => (
                  <TextField {...f} label="Kraj (ISO)" fullWidth />
                )}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
          {hasFieldErrors && (
            <FormHelperText error sx={{ textAlign: 'center', mx: 2 }}>
              Uzupełnij brakujące pola oznaczone na czerwono.
            </FormHelperText>
          )}
          {submitErrorMessage && (
            <FormHelperText error sx={{ textAlign: 'center', mx: 2 }}>
              {submitErrorMessage}
            </FormHelperText>
          )}
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button onClick={onClose} disabled={isLoading}>
              Anuluj
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
            >
              Zapisz
            </Button>
          </Stack>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddReservationPartyModal;
